import React from 'react';
import { Link } from 'react-router-dom';
import {
    Mail,
    Phone,
    MapPin,
    Linkedin,
    Twitter,
    Facebook,
    Instagram,
    ArrowUpRight
} from 'lucide-react';

const Footer = () => {
    const currentYear = new Date().getFullYear();

    const footerLinks = {
        company: [
            { name: 'About Us', path: '/about' },

            { name: 'Contact', path: '/contact' },
        ],
        services: [
            { name: 'Web Development', path: '/services#web' },
            { name: 'App Development', path: '/services#app' },
            { name: 'E-Commerce', path: '/services#ecommerce' },
            { name: 'Software Development', path: '/services#software' },
            { name: 'SEO Optimization', path: '/services#seo' },
            { name: 'Social Media', path: '/services#social' },
        ],
        industries: [
            { name: 'Enterprise', path: '/industries#enterprise' },
            { name: 'Startups', path: '/industries#startups' },
            { name: 'Retail & E-commerce', path: '/industries#retail' },
            { name: 'Technology & SaaS', path: '/industries#saas' },
        ],
    };

    const socialLinks = [
        { icon: Linkedin, href: 'https://www.linkedin.com/in/prime-solutions-a9367b378', label: 'LinkedIn' },
        { icon: Twitter, href: 'https://twitter.com', label: 'X' },
        { icon: Facebook, href: 'https://facebook.com', label: 'Facebook' },
        { icon: Instagram, href: 'https://www.instagram.com/prime_solutions_vn?utm_source=qr&igsh=MTAzM2x4cGc4anYzZw%3D%3D', label: 'Instagram' },
    ];

    return (
        <footer className="bg-secondary text-white">
            {/* Main Footer */}
            <div className="container-custom py-12 md:py-16 lg:py-20">
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-8 lg:gap-8">
                    {/* Company Info */}
                    <div className="sm:col-span-2 lg:col-span-2">
                        <Link to="/" className="inline-flex items-center gap-3 mb-6">
                            <img
                                src="/logo.png"
                                alt="Prime Solutions Logo"
                                className="h-12 md:h-14 w-auto object-contain brightness-10"
                            />
                            <span className="text-2xl font-bold tracking-tight text-white mt-1.5">
                                Prime Solutions
                            </span>
                        </Link>
                        <p className="text-gray-400 mb-6 max-w-sm leading-relaxed text-sm md:text-base">
                            Building scalable digital solutions for modern businesses.
                            We deliver enterprise-grade technology that accelerates growth.
                        </p>
                        <div className="space-y-3">
                            <a
                                href="mailto:primesolutions108@gmail.com"
                                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors text-sm md:text-base"
                            >
                                <Mail size={18} className="flex-shrink-0" />
                                <span>primesolutions108@gmail.com</span>
                            </a>
                            <a
                                href="tel:+917867011695"
                                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors text-sm md:text-base"
                            >
                                <Phone size={18} className="flex-shrink-0" />
                                <span>+91 78670 11695</span>
                            </a>
                            <a
                                href="tel:+919943993393"
                                className="flex items-center space-x-3 text-gray-400 hover:text-white transition-colors text-sm md:text-base"
                            >
                                <Phone size={18} className="flex-shrink-0" />
                                <span>+91 99439 93393</span>
                            </a>
                            <div className="flex items-start space-x-3 text-gray-400 text-sm md:text-base">
                                <MapPin size={18} className="mt-0.5 flex-shrink-0" />
                                <a
                                    href="https://www.google.com/maps/place/PRIME+SOLUTIONS/@11.0177157,76.9683328,17z/data=!4m14!1m7!3m6!1s0x3ba85910e845b985:0xa38b508c0c3678a8!2sPRIME+SOLUTIONS!8m2!3d11.0176428!4d76.9678624!16s%2Fg%2F11pl67ttbx!3m5!1s0x3ba85910e845b985:0xa38b508c0c3678a8!8m2!3d11.0176428!4d76.9678624!16s%2Fg%2F11pl67ttbx?entry=ttu&g_ep=EgoyMDI2MDEyNi4wIKXMDSoASAFQAw%3D%3D"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="hover:text-white transition-colors"
                                >
                                    132, First Floor, 4th Street, Cross Cut Road, Gandhipuram, Coimbatore - 641 012
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Company Links */}
                    <div>
                        <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Company</h4>
                        <ul className="space-y-2 lg:space-y-3">
                            {footerLinks.company.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white transition-colors inline-flex items-center group text-sm md:text-base"
                                    >
                                        {link.name}
                                        <ArrowUpRight
                                            size={14}
                                            className="ml-1 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all"
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Services Links */}
                    <div>
                        <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Services</h4>
                        <ul className="space-y-2 lg:space-y-3">
                            {footerLinks.services.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white transition-colors inline-flex items-center group text-sm md:text-base"
                                    >
                                        {link.name}
                                        <ArrowUpRight
                                            size={14}
                                            className="ml-1 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all"
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>

                    {/* Industries Links */}
                    <div>
                        <h4 className="text-base lg:text-lg font-semibold mb-4 lg:mb-6">Industries</h4>
                        <ul className="space-y-2 lg:space-y-3">
                            {footerLinks.industries.map((link) => (
                                <li key={link.name}>
                                    <Link
                                        to={link.path}
                                        className="text-gray-400 hover:text-white transition-colors inline-flex items-center group text-sm md:text-base"
                                    >
                                        {link.name}
                                        <ArrowUpRight
                                            size={14}
                                            className="ml-1 opacity-0 -translate-y-1 translate-x-1 group-hover:opacity-100 group-hover:translate-y-0 group-hover:translate-x-0 transition-all"
                                        />
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-gray-700">
                <div className="container-custom py-5 md:py-6 flex flex-col md:flex-row justify-between items-center gap-4">
                    <p className="text-gray-400 text-xs md:text-sm text-center md:text-left">
                        © {currentYear} Prime Solutions. All rights reserved.
                    </p>

                    {/* Social Links */}
                    <div className="flex items-center space-x-3 md:space-x-4 order-first md:order-none">
                        {socialLinks.map((social) => (
                            <a
                                key={social.label}
                                href={social.href}
                                target="_blank"
                                rel="noopener noreferrer"
                                aria-label={social.label}
                                className="w-9 h-9 md:w-10 md:h-10 rounded-full bg-gray-700 flex items-center justify-center text-gray-400 hover:bg-primary hover:text-white transition-all duration-300"
                            >
                                {social.label === 'X' ? (
                                    <svg viewBox="0 0 24 24" fill="currentColor" className="w-4 h-4 md:w-[18px] md:h-[18px]">
                                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                                    </svg>
                                ) : (
                                    <social.icon size={16} className="md:w-[18px] md:h-[18px]" />
                                )}
                            </a>
                        ))}
                    </div>

                    {/* Legal Links */}
                    <div className="flex items-center space-x-4 md:space-x-6 text-xs md:text-sm text-gray-400">
                        <Link to="/privacy" className="hover:text-white transition-colors">Privacy Policy</Link>
                        <Link to="/terms" className="hover:text-white transition-colors">Terms of Service</Link>
                    </div>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
